// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { Select } from '@imprivata-cloud/design-system';
import { useEffect, useRef } from 'react';

export type FieldSelectOption = { value: string; label: string };

type FieldSelectProps = {
  label: string;
  // For selects named by the heading above them, such as the grace period's hours and minutes.
  hideLabel?: boolean;
  value: string;
  options: readonly FieldSelectOption[];
  describedBy?: string;
  disabled?: boolean;
  className?: string;
  onChange?: (value: string) => void;
};

// The DS Select names its trigger with `aria-label` but sets the trigger's `aria-describedby`
// only from its own helper and error text, so the description is attached to the trigger here.
const FieldSelect = ({
  label,
  hideLabel = false,
  value,
  options,
  describedBy,
  disabled,
  className,
  onChange,
}: FieldSelectProps) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const trigger = ref.current?.querySelector('[role="combobox"]');
    if (describedBy) {
      trigger?.setAttribute('aria-describedby', describedBy);
    } else {
      trigger?.removeAttribute('aria-describedby');
    }
  }, [describedBy]);

  return (
    <div ref={ref} className={className ? `workflows-select ${className}` : 'workflows-select'}>
      <Select
        label={hideLabel ? undefined : label}
        aria-label={hideLabel ? label : undefined}
        items={[...options]}
        value={value}
        disabled={disabled}
        onSelectionChange={next => {
          if (next !== null) {
            onChange?.(next);
          }
        }}
      />
    </div>
  );
};

export default FieldSelect;
